/**
 * @file src/pages/company-onboarding-wizard/constants.js
 * @description Static data for the company onboarding wizard.
 *
 * Rules:
 *  - No functions, no React, no logic — pure data only
 *  - INITIAL_FORM_DATA is the single source of truth for form shape
 *  - Field names map directly to ZATCA XML fields — comments reference
 *    the exact UBL tag and business rule for each address field
 */

import { Building, MapPin, Phone } from "lucide-react";
import { KSA_COUNTRY_CODE }        from "config/constants";

// ---------------------------------------------------------------------------
// Form
// ---------------------------------------------------------------------------

/**
 * Initial state for the onboarding form.
 * Used by useOnboardingForm to initialise and reset form data.
 *
 * Address fields map to ZATCA XML Implementation Standard v1.2:
 *  buildingNumber     → cbc:BuildingNumber      (BR-KSA-37: exactly 4 digits)
 *  street             → cbc:StreetName           (BR-KSA-09: mandatory)
 *  district           → cbc:CitySubdivisionName  (BR-KSA-09: mandatory)
 *  plotIdentification → cbc:PlotIdentification   (optional, max 4 digits)
 *  city               → cbc:CityName             (BR-KSA-09: mandatory)
 *  countrySubentity   → cbc:CountrySubentity     (province / region)
 *  postalCode         → cbc:PostalZone           (BR-KSA-66: exactly 5 digits)
 *  country            → cbc:IdentificationCode   (BR-09: fixed "SA")
 */
export const INITIAL_FORM_DATA = {
  // ── Step 1 — Company Info ─────────────────────────────────────────────
  legalName:          "",
  vatNumber:          "",
  registrationNumber: { id: "", schemeId: "" },
  logo:               null,

  // ── Step 2 — Address ──────────────────────────────────────────────────
  buildingNumber:     "",
  street:             "",
  district:           "",
  plotIdentification: "",
  city:               "",
  countrySubentity:   "",
  postalCode:         "",
  country:            KSA_COUNTRY_CODE,

  // ── Step 3 — Contact (optional) ───────────────────────────────────────
  phone:              "",
  email:              "",
};

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

/**
 * Wizard step definitions.
 * Used by WizardShell to render the progress bar and step titles.
 */
export const STEPS = [
  {
    number:      1,
    title:       "Company Info",
    description: "Legal name, VAT & registration",
    icon:        Building,
  },
  {
    number:      2,
    title:       "Address",
    description: "Saudi Arabia national address",
    icon:        MapPin,
  },
  {
    number:      3,
    title:       "Contact",
    description: "Optional contact details",
    icon:        Phone,
  },
];

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

/**
 * Tab definitions for Create vs Join company.
 */
export const TABS = {
  CREATE: "create",
  JOIN:   "join",
};